import React from 'react';
import { ProjectWithItems } from '../../services/projectService';
import './ProjectDetailsModal.css';

interface ProjectDetailsModalProps {
  projectData: ProjectWithItems;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectDetailsModal: React.FC<ProjectDetailsModalProps> = ({ projectData, isOpen, onClose }) => {
  if (!isOpen) return null;

  const { project, data_files, datasets, training, models, inference } = projectData;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="project-modal-overlay" onClick={onClose}>
      <div className="project-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="project-modal-header">
          <div>
            <h2>{project.display_name || project.name}</h2>
            {project.description && <p className="project-modal-description">{project.description}</p>}
          </div>
          <button className="project-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="project-modal-content">
          <div className="project-pipeline-sections">
            {/* Data Section */}
            <div className="pipeline-section-box">
              <div className="pipeline-section-header">
                <span className="pipeline-section-icon">📊</span>
                <h3>Data</h3>
                <span className="pipeline-section-count">{data_files.length}</span>
              </div>
              <div className="pipeline-section-items">
                {data_files.length > 0 ? (
                  <ul className="pipeline-items-list">
                    {data_files.map((item) => (
                      <li key={item.id} className="pipeline-item">
                        <div className="pipeline-item-name">{item.name}</div>
                        <div className="pipeline-item-meta">
                          <span className="pipeline-item-type">{item.data_type}</span>
                          {item.sample_count && (
                            <span className="pipeline-item-count">{item.sample_count} samples</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="pipeline-section-empty">No data files</div>
                )}
              </div>
            </div>

            {/* Datasets Section */}
            <div className="pipeline-section-box">
              <div className="pipeline-section-header">
                <span className="pipeline-section-icon">📁</span>
                <h3>Datasets</h3>
                <span className="pipeline-section-count">{datasets.length}</span>
              </div>
              <div className="pipeline-section-items">
                {datasets.length > 0 ? (
                  <ul className="pipeline-items-list">
                    {datasets.map((item) => (
                      <li key={item.id} className="pipeline-item">
                        <div className="pipeline-item-name">{item.display_name || item.name}</div>
                        <div className="pipeline-item-meta">
                          {item.dataset_type && (
                            <span className="pipeline-item-type">{item.dataset_type}</span>
                          )}
                          {item.total_samples && (
                            <span className="pipeline-item-count">{item.total_samples} samples</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="pipeline-section-empty">No datasets</div>
                )}
              </div>
            </div>

            {/* Training Section */}
            <div className="pipeline-section-box">
              <div className="pipeline-section-header">
                <span className="pipeline-section-icon">🏋️</span>
                <h3>Training</h3>
                <span className="pipeline-section-count">{training.length}</span>
              </div>
              <div className="pipeline-section-items">
                {training.length > 0 ? (
                  <ul className="pipeline-items-list">
                    {training.map((item) => (
                      <li key={item.id} className="pipeline-item">
                        <div className="pipeline-item-name">{item.display_name || item.name}</div>
                        <div className="pipeline-item-meta">
                          <span className={`pipeline-item-status status-${item.status || 'pending'}`}>
                            {item.status || 'pending'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="pipeline-section-empty">No training configurations</div>
                )}
              </div>
            </div>

            {/* Models Section */}
            <div className="pipeline-section-box">
              <div className="pipeline-section-header">
                <span className="pipeline-section-icon">🧠</span>
                <h3>Models</h3>
                <span className="pipeline-section-count">{models.length}</span>
              </div>
              <div className="pipeline-section-items">
                {models.length > 0 ? (
                  <ul className="pipeline-items-list">
                    {models.map((item) => (
                      <li key={item.id} className="pipeline-item">
                        <div className="pipeline-item-name">{item.display_name || item.name}</div>
                        <div className="pipeline-item-meta">
                          <span className={`pipeline-item-status status-${item.training_status || 'not_trained'}`}>
                            {item.training_status || 'not_trained'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="pipeline-section-empty">No models</div>
                )}
              </div>
            </div>

            {/* Inference Section */}
            <div className="pipeline-section-box">
              <div className="pipeline-section-header">
                <span className="pipeline-section-icon">🚀</span>
                <h3>Inference</h3>
                <span className="pipeline-section-count">{inference.length}</span>
              </div>
              <div className="pipeline-section-items">
                {inference.length > 0 ? (
                  <ul className="pipeline-items-list">
                    {inference.map((item) => (
                      <li key={item.id} className="pipeline-item">
                        <div className="pipeline-item-name">{item.display_name || item.name}</div>
                        <div className="pipeline-item-meta">
                          <span className={`pipeline-item-status status-${item.status || 'active'}`}>
                            {item.status || 'active'}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="pipeline-section-empty">No inference configurations</div>
                )}
              </div>
            </div>
          </div>

          <div className="project-modal-footer">
            <div className="project-modal-meta">
              <span>Created: {formatDate(project.created_at)}</span>
              {project.updated_at && project.updated_at !== project.created_at && (
                <span>Updated: {formatDate(project.updated_at)}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsModal;

